import { FC } from "react";

import CustomLink from "@Components/CustomLink";
import Image from "@Components/Image";
import Modal from "@Components/Modal";
import { CHINA_FOOTER_QR_TILES } from "@Components/FooterV2/constant";
import config from "@Config/index";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import {
  APP_DOWNLOAD_APP_STORE_LINK,
  APP_DOWNLOAD_PLAY_STORE_LINK,
} from "constants/configConstants";

// The branded QR: the SAME Singular link the site's desktop app nudge encodes
// (…/Ajzr5/ha57, deep-linking to Home), regenerated at error-correction level H with
// the amber app icon (Figma ApmJcAHWKB8FAEyu2AcqQn, node 5742:43629) in the centre.
// Version 9 (53 modules) rather than the minimum 8, so the larger 15-module icon still
// leaves spare error correction: ZXing-verified through 5–30° tilt, 2px blur and 150px.
import amberQr from "@Pages/HomeV3/assets/qr-amber-app.svg";
import appStoreBadge from "@Pages/HomeV3/assets/badge-app-store.svg";
import googlePlayBadge from "@Pages/HomeV3/assets/badge-google-play.png";
import ChinaAppStores from "./ChinaAppStores";
import styles from "./AppDownloadModal.module.scss";

/**
 * "Get the amber app" — opened from the footer's QR card.
 *
 * The QR (branded — see the import) sits in a scanner —
 * smooth SVG corner brackets, a gradient beam with a faint grid trail sweeping down and back up,
 * and below it are the official App Store and Google
 * Play badges —
 * Apple's and Google's own artwork, unaltered — for anyone reading on the phone
 * they'd scan with.
 */
interface Props {
  onClose: () => void;
  onStoreClick: (store: string) => void;
}

/**
 * The .cn modal, as amberstudent.cn's header offers it: the app's download QR beside the
 * WeChat mini-program's (the same two codes as the China footer), then the China app
 * stores (ChinaAppStores) in place of Google Play, which isn't reachable there. Copy is
 * the live China modal's own keys.
 */
const ChinaAppDownload: FC<Pick<Props, "onStoreClick">> = ({ onStoreClick }) => {
  const { t } = useTranslationHook();
  const app = CHINA_FOOTER_QR_TILES.find((tile) => tile.id === "app-download");
  const mini = CHINA_FOOTER_QR_TILES.find((tile) => tile.id === "miniprogram");
  return (
    <div className={styles.body}>
      <h3 className={styles.title}>{t("header.downloadapp.title")}</h3>
      <div className={styles.cnQrs}>
        {[
          { tile: app, label: t("header.downloadapp.scan_qr") },
          { tile: mini, label: t("header.downloadapp.scan_miniprogram") },
        ].map(
          ({ tile, label }) =>
            tile && (
              <figure key={tile.id} className={styles.cnQr}>
                {/* Raw src: a resize modifier softens a QR code's modules past scanning. */}
                <Image src={tile.src} alt={label} width="128" height="128" isNotLazy />
                <figcaption>{label}</figcaption>
              </figure>
            ),
        )}
      </div>
      <div className={styles.divider}>
        <span>{t("header.downloadapp.download_from")}</span>
      </div>
      <ChinaAppStores onStoreClick={onStoreClick} centered />
    </div>
  );
};

const AppDownloadModal: FC<Props> = ({ onClose, onStoreClick }) => (
  <Modal size="fitContent" align="center" hideModal={onClose}>
    {config.IS_CHINA_APP ? (
      <ChinaAppDownload onStoreClick={onStoreClick} />
    ) : (
      <div className={styles.body}>
        <h3 className={styles.title}>Get the amber app</h3>
        <p className={styles.sub}>Scan with your phone&apos;s camera to download</p>

        {/* The scanner. Corners are SVG arcs (smooth, round-capped) that breathe gently;
        the QR sits in its own clipped window, and the beam + its grid trail sweep
        down through that window only. */}
        <div className={styles.scanner}>
          <svg className={styles.corners} viewBox="0 0 248 248" aria-hidden="true">
            <path d="M6 46V26A20 20 0 0 1 26 6h20" />
            <path d="M202 6h20a20 20 0 0 1 20 20v20" />
            <path d="M242 202v20a20 20 0 0 1-20 20h-20" />
            <path d="M46 242H26A20 20 0 0 1 6 222v-20" />
          </svg>
          <div className={styles.window}>
            <Image
              src={amberQr}
              alt="QR code to download the amber app"
              width="184px"
              height="184px"
              className={styles.qr}
              isNotLazy
            />
            {/* The sweep is a zero-height marker at the beam's line. Two trails hang off
            it — one above (shown going down), one below (shown going up) — and
            cross-fade at the ends, so nothing ever flips or jumps. */}
            <span className={styles.sweep} aria-hidden="true">
              <span className={`${styles.trail} ${styles.trailAbove}`} />
              <span className={`${styles.trail} ${styles.trailBelow}`} />
              <span className={styles.beam} />
            </span>
          </div>
        </div>

        <div className={styles.divider}>
          <span>or download from</span>
        </div>

        <div className={styles.badges}>
          <CustomLink
            href={APP_DOWNLOAD_APP_STORE_LINK}
            target="_blank"
            rel="noreferrer noopener"
            isExternal
            className={styles.badge}
            dataTestId="v2-app-modal-app-store"
            onClick={() => onStoreClick("App Store")}
          >
            <Image
              src={appStoreBadge}
              alt="Download on the App Store"
              width="135px"
              height="45px"
            />
          </CustomLink>
          <CustomLink
            href={APP_DOWNLOAD_PLAY_STORE_LINK}
            target="_blank"
            rel="noreferrer noopener"
            isExternal
            className={styles.badge}
            dataTestId="v2-app-modal-google-play"
            onClick={() => onStoreClick("Google Play")}
          >
            <Image src={googlePlayBadge} alt="Get it on Google Play" width="151px" height="45px" />
          </CustomLink>
        </div>
      </div>
    )}
  </Modal>
);

export default wrapperHOC(AppDownloadModal, {
  componentName: "AppDownloadModal-HomeV2Footer",
  showForChina: true,
});
